import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, LeafIcon, TreePineIcon, RecycleIcon, ClipboardCheckIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Environmental Protection
{
  id: 'ep1',
  title: 'Environmental Impact Assessment',
  description: 'Conduct environmental impact studies for development projects',
  category: 'Environmental Protection',
  estimatedTime: '20-25 business days',
  fee: '$750.00'
}, {
  id: 'ep2',
  title: 'Wildlife Protection Report',
  description: 'Report wildlife concerns or request habitat protection',
  category: 'Environmental Protection',
  estimatedTime: '5-7 business days',
  fee: 'No fee'
}, {
  id: 'ep3',
  title: 'Air Quality Monitoring',
  description: 'Request air quality testing and monitoring services',
  category: 'Environmental Protection',
  estimatedTime: '7-10 business days',
  fee: '$200.00'
}, {
  id: 'ep4',
  title: 'Water Quality Protection',
  description: 'Report water pollution or request water quality assessment',
  category: 'Environmental Protection',
  estimatedTime: '5-7 business days',
  fee: '$150.00'
},
// Conservation Programs
{
  id: 'cp1',
  title: 'Tree Planting Initiative',
  description: 'Participate in community tree planting programs',
  category: 'Conservation Programs',
  estimatedTime: '3-5 business days',
  fee: 'No fee'
}, {
  id: 'cp2',
  title: 'Habitat Restoration',
  description: 'Join local habitat restoration projects',
  category: 'Conservation Programs',
  estimatedTime: '10-15 business days',
  fee: 'No fee'
}, {
  id: 'cp3',
  title: 'Species Conservation',
  description: 'Support endangered species protection programs',
  category: 'Conservation Programs',
  estimatedTime: '5-7 business days',
  fee: 'Voluntary donation'
}, {
  id: 'cp4',
  title: 'Green Space Preservation',
  description: 'Apply for green space conservation designation',
  category: 'Conservation Programs',
  estimatedTime: '15-20 business days',
  fee: '$300.00'
},
// Sustainability Initiatives
{
  id: 'si1',
  title: 'Energy Efficiency Program',
  description: 'Access renewable energy and efficiency resources',
  category: 'Sustainability Initiatives',
  estimatedTime: '5-7 business days',
  fee: 'Varies by program'
}, {
  id: 'si2',
  title: 'Green Building Certification',
  description: 'Apply for green building certification and assessment',
  category: 'Sustainability Initiatives',
  estimatedTime: '15-20 business days',
  fee: '$500.00'
}, {
  id: 'si3',
  title: 'Waste Reduction Support',
  description: 'Access resources for waste reduction and recycling',
  category: 'Sustainability Initiatives',
  estimatedTime: '3-5 business days',
  fee: 'No fee'
}, {
  id: 'si4',
  title: 'Sustainable Transport',
  description: 'Join sustainable transportation initiatives',
  category: 'Sustainability Initiatives',
  estimatedTime: '5-7 business days',
  fee: 'Varies by program'
},
// Environmental Permits & Compliance
{
  id: 'pc1',
  title: 'Environmental Permit Application',
  description: 'Apply for environmental activity permits',
  category: 'Environmental Permits & Compliance',
  estimatedTime: '10-15 business days',
  fee: '$400.00'
}, {
  id: 'pc2',
  title: 'Compliance Inspection',
  description: 'Schedule environmental compliance inspection',
  category: 'Environmental Permits & Compliance',
  estimatedTime: '7-10 business days',
  fee: '$250.00'
}, {
  id: 'pc3',
  title: 'Environmental Reporting',
  description: 'Submit required environmental compliance reports',
  category: 'Environmental Permits & Compliance',
  estimatedTime: '5-7 business days',
  fee: '$150.00'
}, {
  id: 'pc4',
  title: 'Violation Resolution',
  description: 'Address environmental compliance violations',
  category: 'Environmental Permits & Compliance',
  estimatedTime: '10-15 business days',
  fee: 'Varies by case'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Environmental Protection':
      return <LeafIcon className="h-6 w-6 text-green-600" data-id="element-3723" />;
    case 'Conservation Programs':
      return <TreePineIcon className="h-6 w-6 text-blue-600" data-id="element-3724" />;
    case 'Sustainability Initiatives':
      return <RecycleIcon className="h-6 w-6 text-purple-600" data-id="element-3725" />;
    case 'Environmental Permits & Compliance':
      return <ClipboardCheckIcon className="h-6 w-6 text-orange-600" data-id="element-3726" />;
    default:
      return <LeafIcon className="h-6 w-6 text-gray-600" data-id="element-3727" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-3728">
      <div className="p-5 flex flex-col h-full" data-id="element-3729">
        <div className="flex-1" data-id="element-3730">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3731">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-3732">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-3733">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-3734">
            <div className="flex items-center" data-id="element-3735">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3736">
                ⏱
              </span>
              <span data-id="element-3737">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-3738">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3739">
                💰
              </span>
              <span data-id="element-3740">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-3741">
            Access Service
            <span className="ml-1" data-id="element-3742">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-3743">
      <div className="flex items-center mb-4" data-id="element-3744">
        <CategoryIcon category={category} data-id="element-3745" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-3746">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3747">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-3748" />)}
      </div>
    </div>;
};
export const EnvironmentalPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-3749">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3750">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3751">
          <a href="/" className="hover:text-gray-700" data-id="element-3752">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3753" />
          <span className="text-gray-900" data-id="element-3754">Environmental Services</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3755">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3756">
            Environmental Services
          </h1>
          <p className="text-gray-600" data-id="element-3757">
            Access environmental protection, conservation programs, and
            sustainability initiatives
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-3758">
          <div className="max-w-md relative" data-id="element-3759">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3760">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3761" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3762" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-3763" />)}
      </div>
    </div>;
};